import { useCallback, useEffect, useRef, useState } from "react";

export type SaveStatus = "saved" | "pending" | "saving" | "error";

const DELAY_MS = 800;

export const useAutosave = <T>(value: T, save: (value: T) => Promise<unknown>) => {
  const [status, setStatus] = useState<SaveStatus>("saved");
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(value);
  const dirty = useRef(false);
  const saving = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const inFlight = useRef<Promise<unknown>>(Promise.resolve());
  const seen = useRef(value);

  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    await inFlight.current.catch(() => undefined);
    if (!dirty.current) return;
    dirty.current = false;
    setStatus("saving");
    saving.current = true;
    inFlight.current = save(latest.current).finally(() => (saving.current = false)).then(
      () => {
        setError(null);
        if (!dirty.current) setStatus("saved");
      },
      (e) => {
        dirty.current = true;
        setStatus("error");
        setError((e as Error).message);
        throw e;
      }
    );
    await inFlight.current;
  }, [save]);

  useEffect(() => {
    // Compared with the previous value, not the loaded one: undo can return to the loaded object.
    if (value === seen.current) return;
    seen.current = value;
    latest.current = value;
    dirty.current = true;
    setStatus("pending");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => flush().catch(() => undefined), DELAY_MS);
  }, [value, flush]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty.current || saving.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  return { status, error, flush };
};
